import {
  STATUS_DE_SINISTRO,
  type StatusDeSinistro,
  TIPOS_DE_SINISTRO,
  type TipoDeSinistro,
} from '@locaveri/shared'
import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { z } from 'zod'

import { paginaDe } from '@/features/precos/api'
import { apiGet, apiPatch, apiPost } from '@/lib/api'
import { ROTAS_DE_ARQUIVOS, ROTAS_DE_SINISTROS } from '@/lib/endpoints'
import { montarQuery } from '@/lib/query-string'

/**
 * Sinistros — Adendo D (Fase 4).
 *
 * ---------------------------------------------------------------------------
 * AQUI A RESERVA É DIGITADA — E É DE PROPÓSITO
 * ---------------------------------------------------------------------------
 * Diferente da multa, o registro de sinistro ACEITA `reservationId`: sinistro
 * nem sempre acontece em locação (pátio, translado), e quando acontece, quem
 * sabe qual reserva é quem está com o boletim na mão. A defesa contra o
 * vínculo errado não é esconder o campo — é o SERVIÇO exigir que a reserva
 * apontada tenha tido este carro naquela data, e o 422 dele vira toast.
 *
 * ---------------------------------------------------------------------------
 * `responsabilidadeDoCliente` É O CAMPO QUE O FECHAMENTO LÊ
 * ---------------------------------------------------------------------------
 * É ele que autoriza a franquia a virar linha de cobrança. Por isso aparece
 * na resposta mesmo quando `false` (ausência de responsabilidade também é uma
 * decisão registrada) e por isso o formulário o trata como decisão explícita,
 * não como checkbox decorativo.
 *
 * O modelo de LEITURA espelha `sinistros.dto.ts`; os contratos de ENTRADA
 * (`registrarSinistroSchema`, `atualizarSinistroSchema`) vêm de
 * `@locaveri/shared`. A franquia chega como STRING de centavos (invariante
 * nº 1) e é formatada por `lib/dinheiro`.
 */

// ---------------------------------------------------------------------------
// Modelo de leitura
// ---------------------------------------------------------------------------

export const sinistroSchema = z.object({
  id: z.uuid(),
  vehicleId: z.uuid(),
  veiculoPlaca: z.string(),
  /** `null` quando o sinistro aconteceu fora de locação (pátio, translado). */
  reservationId: z.uuid().nullable(),

  tipo: z.enum(TIPOS_DE_SINISTRO),
  status: z.enum(STATUS_DE_SINISTRO),

  dataDoSinistro: z.string(),
  descricao: z.string(),

  boletimDeOcorrencia: z.string().nullable(),
  /** Ids de arquivos no storage — a tela de fotos é de outra fase. */
  fotos: z.array(z.string()),

  seguradoraAcionada: z.boolean(),
  /** Franquia da apólice, centavos como string. `null` = não conhecida. */
  franquiaDaApolice: z.string().nullable(),
  /** `true` quando a apuração atribuiu ao cliente — autoriza a cobrança. */
  responsabilidadeDoCliente: z.boolean(),

  observacoes: z.string().nullable(),

  criadoEm: z.string(),
  atualizadoEm: z.string(),
})

export type Sinistro = z.infer<typeof sinistroSchema>

export const paginaDeSinistrosSchema = paginaDe(sinistroSchema)

export type PaginaDeSinistros = z.infer<typeof paginaDeSinistrosSchema>

/**
 * Corpo já validado por um schema de `@locaveri/shared`, pronto para o fio.
 * Mapa aberto pelo motivo de sempre: `moneySchemaNonNegative` transforma a
 * franquia em `bigint` e `instanteSchema` a data em `Date` — o que viaja é a
 * ENTRADA validada, nunca `validado.data`.
 */
export type CorpoDeSinistro = Readonly<Record<string, unknown>>

// ---------------------------------------------------------------------------
// Filtros
// ---------------------------------------------------------------------------

export interface FiltrosDeSinistros {
  readonly pagina: number
  readonly tamanho: number
  readonly status: StatusDeSinistro | ''
  readonly tipo: TipoDeSinistro | ''
  readonly vehicleId: string
}

// ---------------------------------------------------------------------------
// Chave de cache
// ---------------------------------------------------------------------------

export const CHAVE_DE_SINISTROS = 'sinistros'

// ---------------------------------------------------------------------------
// Leitura
// ---------------------------------------------------------------------------

export function useSinistros(filtros: FiltrosDeSinistros): UseQueryResult<PaginaDeSinistros> {
  return useQuery({
    queryKey: [CHAVE_DE_SINISTROS, filtros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        status: filtros.status,
        tipo: filtros.tipo,
        vehicleId: filtros.vehicleId,
      })

      return apiGet(`${ROTAS_DE_SINISTROS.lista}${query}`, paginaDeSinistrosSchema, { signal })
    },
    // Mantém a página anterior enquanto a nova carrega: sem isso a tabela
    // pisca em branco a cada mudança de filtro.
    placeholderData: (anterior) => anterior,
  })
}

// ---------------------------------------------------------------------------
// Escrita
// ---------------------------------------------------------------------------

export function useRegistrarSinistro(): UseMutationResult<Sinistro, Error, CorpoDeSinistro> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: CorpoDeSinistro) =>
      apiPost(ROTAS_DE_SINISTROS.lista, sinistroSchema, dados),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_SINISTROS] })
    },
  })
}

export interface AtualizacaoDeSinistro {
  readonly id: string
  readonly dados: CorpoDeSinistro
}

export function useAtualizarSinistro(): UseMutationResult<Sinistro, Error, AtualizacaoDeSinistro> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, dados }: AtualizacaoDeSinistro) =>
      apiPatch(ROTAS_DE_SINISTROS.porId(id), sinistroSchema, dados),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_SINISTROS] })
    },
  })
}

// ---------------------------------------------------------------------------
// Upload assinado de foto
// ---------------------------------------------------------------------------

/**
 * O que `POST /arquivos/foto-de-sinistro` devolve — espelho de `arquivos.dto.ts`,
 * o mesmo formato das outras finalidades de upload.
 *
 * O binário nunca toca a API: ela só ASSINA a URL, e o `PUT` no storage é um
 * `fetch` cru de propósito — o MinIO/S3 não fala o envelope de erro da API,
 * não quer Bearer (mandar o access token para outro host seria vazá-lo) e a
 * assinatura exige EXATAMENTE os cabeçalhos devolvidos, nem um a mais.
 */
const uploadAssinadoSchema = z.object({
  /** `PUT` aqui, com exatamente os cabeçalhos abaixo. */
  urlDeUpload: z.string(),
  cabecalhos: z.record(z.string(), z.string()),
  urlDoArquivo: z.string(),
  chave: z.string(),
  expiraEmSegundos: z.number().int().positive(),
})

export interface FotoDeSinistroEnviada {
  /**
   * A CHAVE do storage (`tenants/<uuid>/<pasta>/<uuid>.<ext>`) — é ELA que
   * `registrarSinistroSchema.fotos` espera, ao contrário da vistoria, que
   * grava o UUID extraído. Só com a chave a API assina a URL de LEITURA
   * depois (`useUrlsDeLeitura`).
   */
  readonly chave: string
  /** URL pública de curta duração, para a miniatura sem baixar do disco de novo. */
  readonly urlDoArquivo: string
}

/** O `PUT` do binário, direto no storage. Ver o comentário do schema acima. */
async function enviarParaOStorage(
  urlDeUpload: string,
  cabecalhos: Readonly<Record<string, string>>,
  arquivo: Blob,
): Promise<void> {
  let resposta: Response

  try {
    resposta = await fetch(urlDeUpload, { method: 'PUT', headers: cabecalhos, body: arquivo })
  } catch {
    throw new Error('Não foi possível falar com o armazenamento de arquivos. Confira a conexão.')
  }

  if (!resposta.ok) {
    throw new Error(
      `O armazenamento recusou o arquivo (HTTP ${String(resposta.status)}). Tente de novo.`,
    )
  }
}

/**
 * Upload em dois passos: a API assina (`POST` autenticado, onde o teto de
 * tamanho e o tipo MIME são validados por finalidade) e o navegador faz o
 * `PUT` direto no storage. O que volta é a CHAVE pronta para o corpo do
 * registro de sinistro.
 */
export function useEnviarFotoDeSinistro(): UseMutationResult<FotoDeSinistroEnviada, Error, Blob> {
  return useMutation({
    mutationFn: async (arquivo: Blob) => {
      const assinado = await apiPost(ROTAS_DE_ARQUIVOS.fotoDeSinistro, uploadAssinadoSchema, {
        tipoMime: arquivo.type,
        tamanhoEmBytes: arquivo.size,
      })

      await enviarParaOStorage(assinado.urlDeUpload, assinado.cabecalhos, arquivo)

      return { chave: assinado.chave, urlDoArquivo: assinado.urlDoArquivo }
    },
  })
}
